/**
 * The frame every signed-in screen sits in (docs/07-gui-proposal §2). Since 12/09/2026 the left
 * navigation runs the FULL height of the window and the top bar sits beside it, over the page
 * only (docs/13-tasks/01-ui-chrome.md#18 §2): identity and navigation belong together on the left, the top bar
 * keeps what belongs to the page — period, freshness, theme.
 *
 * At 900 px the navigation leaves the flow and becomes a drawer the ☰ button opens
 * (docs/13-tasks/01-ui-chrome.md#16 M1), so the page gets the whole width and the body never scrolls sideways (§5).
 *
 * TWO pieces of state live here, and each for the same reason — more than one child needs it:
 *   · `navOpen` (the drawer) is shared by the ☰, the nav and the scrim that closes it. Picking a
 *     screen closes it — landing on a screen with the menu still over it is the one thing a
 *     drawer must not do. That is done on the nav link's own click and NOT by watching the route
 *     in an effect: while the drawer is open the scrim covers the page, so a nav link is the only
 *     thing that can navigate anyway.
 *   · `rail` (collapsed to icons) is written onto `.app` as `data-nav`, because the grid COLUMN
 *     is what changes width, and the column belongs to the shell, not to the nav. It is
 *     remembered across reloads by `useNavRail`.
 *
 * The frame is painted by the `.app` class of app.css, not by utility classes: the approved
 * mockup declares the grid once and every measurement here has to match it (docs/13-tasks/01-ui-chrome.md#13 §5).
 *
 * A session is required to be here: 401 is not an error to draw, it is the sign-in screen. The
 * address the reader wanted is carried along so they land on it after signing in.
 */
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { EmptyState } from '../components/EmptyState.js';
import { useNavRail } from '../hooks/useNavRail.js';
import { useSession } from '../hooks/useSession.js';
import { navRailAttribute } from '../utils/navRail.js';
import { SideNav } from './SideNav.js';
import { TopBar } from './TopBar.js';

export function AppShell() {
  const { user, isLoading, signedOut, error } = useSession();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const { rail, toggle: toggleRail } = useNavRail();

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [navOpen]);

  if (isLoading)
    return <p className="p-6 text-[13px] text-muted-foreground">Đang tải phiên đăng nhập…</p>;

  if (signedOut)
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;

  if (user === null)
    return (
      <main className="p-6">
        <EmptyState
          title={error?.message ?? 'Không đọc được phiên đăng nhập'}
          hint={error?.serverDown === true ? 'Máy chủ API chưa chạy.' : undefined}
        />
      </main>
    );

  return (
    <div className="app" data-nav={navRailAttribute(rail)}>
      <TopBar
        navOpen={navOpen}
        onToggleNav={() => {
          setNavOpen((open) => !open);
        }}
      />
      {navOpen && (
        <div
          className="navscrim"
          role="presentation"
          onClick={() => {
            setNavOpen(false);
          }}
        />
      )}
      <SideNav
        user={user}
        rail={rail}
        open={navOpen}
        onToggleRail={toggleRail}
        onPick={() => {
          setNavOpen(false);
        }}
      />
      <main>
        <Outlet context={user} />
      </main>
    </div>
  );
}
